import React, { FC, useState } from 'react';
import classnames from 'classnames';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { IImage, IProductLabel } from 'api/types';
import { WiseLabel } from 'components/wise';
import productNoImage from 'assets/images/product-no-image.png';

import styles from 'theme/pages/wise/WiseProduct/components/WiseGallery/WiseGallery.module.scss';

interface IProps {
  images: IImage[];
  labels: IProductLabel[];
}

export type WiseGalleryProps = IProps;

const WiseGallery: FC<IProps> = ({ images, labels }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Indeksy slajdów, dla których ładowanie obrazka padło (404, broken URL).
  // Trzymamy osobno dla głównego widoku i miniaturek, bo używają innych rozmiarów (big vs thumb).
  const [bigErrors, setBigErrors] = useState<Set<number>>(new Set());
  const [thumbErrors, setThumbErrors] = useState<Set<number>>(new Set());

  const slidesToShow = Math.min(4, images.length);
  const slideCount = images.length;

  const markError = (
    setter: React.Dispatch<React.SetStateAction<Set<number>>>,
    index: number
  ) => {
    setter((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  const goPrev = () => {
    if (slideCount <= 1) return;
    setCurrentImageIndex((i) => (i - 1 + slideCount) % slideCount);
  };

  const goNext = () => {
    if (slideCount <= 1) return;
    setCurrentImageIndex((i) => (i + 1) % slideCount);
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Wise-WiseProduct-components-WiseGallery'
      )}>
      <div className={styles.mainImageWrap}>
        <div className={styles['main-image']}>
          <div className={styles.mainImageViewport}>
            {slideCount > 0 ? (
              <div
                className={styles.mainImageTrack}
                style={{
                  width: `${slideCount * 100}%`,
                  transform: `translateX(-${(currentImageIndex * 100) / slideCount}%)`
                }}>
                {images.map((image, index) => {
                  // Fallback do globalnego placeholdera, gdy:
                  // 1) backend nie ma URL-a (truncate product_image, brak grafiki dla wariantu),
                  // 2) URL jest, ale obraz nie ładuje się (404 / broken link) - patrz onError.
                  const src = !image.big || bigErrors.has(index) ? productNoImage : image.big;
                  return (
                    <div
                      key={index}
                      className={styles.mainImageSlide}
                      style={{ width: `${100 / slideCount}%` }}>
                      <img
                        className={styles.mainImageImg}
                        src={src}
                        alt=""
                        loading={index === 0 ? 'eager' : 'lazy'}
                        onError={() => markError(setBigErrors, index)}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              // Placeholder gdy produkt nie ma żadnych zdjęć w odpowiedzi z API.
              // Spójny z resztą aplikacji (WiseProductItem, CompareBar, MostBoughtProducts).
              <div className={styles.mainImageSlide}>
                <img
                  className={styles.mainImageImg}
                  src={productNoImage}
                  alt="Brak zdjęcia"
                />
              </div>
            )}
          </div>
          <div className={styles.mainImageLabels}>
            {labels.map((label) => (
              <WiseLabel key={label.type} label={label} />
            ))}
          </div>
        </div>

        {images.length > 1 && (
          <>
            <button
              type="button"
              className={classnames(styles.mainNavButton, styles.mainNavPrev)}
              onClick={goPrev}
              aria-label="Poprzednie zdjęcie">
              <ChevronLeft />
            </button>
            <button
              type="button"
              className={classnames(styles.mainNavButton, styles.mainNavNext)}
              onClick={goNext}
              aria-label="Następne zdjęcie">
              <ChevronRight />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <Slider
          dots={false}
          infinite={false}
          arrows={images.length > slidesToShow}
          slidesToShow={slidesToShow}
          slidesToScroll={1}>
          {images.map((image, index) => {
            const src = !image.thumb || thumbErrors.has(index) ? productNoImage : image.thumb;
            return (
              <div className={styles.thumb} key={index} onClick={() => setCurrentImageIndex(index)}>
                <div
                  className={classnames(styles.image, {
                    [styles.active]: index === currentImageIndex
                  })}>
                  <img
                    className={styles.thumbImg}
                    src={src}
                    alt=""
                    loading="lazy"
                    onError={() => markError(setThumbErrors, index)}
                  />
                </div>
              </div>
            );
          })}
        </Slider>
      )}
    </div>
  );
};

export default WiseGallery;
